import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    Container,
    Divider,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { RecentTeam } from "../../model/Dto/ReсentTeamsDto";
import { Api } from "../../request/api/api";
import ContainerWithCircularProgress from "../../components/ContainerWithCircularProgress";

type SortCol = "rating" | "lastEditTime";

const cellSx = { color: "white", borderColor: "rgba(255,255,255,0.08)" };
const headCellSx = { color: "secondary.main", borderColor: "rgba(255,255,255,0.15)", fontWeight: 600, p: "8px 16px" };

const darkInputSx = {
    "& .MuiOutlinedInput-root": {
        color: "white",
        "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
        "&.Mui-focused fieldset": { borderColor: "secondary.main" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "secondary.main" },
    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" },
};

const getDate = (unixSeconds: number): string => {
    const date = new Date(unixSeconds * 1000);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).valueOf();
    return date.toLocaleString(
        "ru-RU",
        date.valueOf() < today
            ? { year: "numeric", month: "numeric", day: "numeric" }
            : { hour: "numeric", minute: "numeric" }
    );
};

export default function LeaderboardPage() {
    const [teams, setTeams] = useState<RecentTeam[] | null>(null);
    const [nameFilter, setNameFilter] = useState("");
    const [factionFilter, setFactionFilter] = useState("");
    const [ownerFilter, setOwnerFilter] = useState("");
    const [sortBy, setSortBy] = useState<SortCol>("rating");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    useEffect(() => {
        Api.team.getLeaderboard().then(setTeams);
    }, []);

    const factionOptions = useMemo(
        () => Array.from(new Set((teams ?? []).map(t => t.faction.name))).sort(),
        [teams]
    );
    const ownerOptions = useMemo(
        () => Array.from(new Set((teams ?? []).map(t => t.creator.name))).sort(),
        [teams]
    );

    const filtered = useMemo(() => {
        if (!teams) return [];
        const q = nameFilter.toLowerCase();
        return [...teams]
            .filter(t => !q || t.name.toLowerCase().includes(q))
            .filter(t => !factionFilter || t.faction.name === factionFilter)
            .filter(t => !ownerFilter || t.creator.name === ownerFilter)
            .sort((a, b) => {
                const mul = sortDir === "asc" ? 1 : -1;
                return sortBy === "rating"
                    ? (a.rating - b.rating) * mul
                    : (a.lastEditTime - b.lastEditTime) * mul;
            });
    }, [teams, nameFilter, factionFilter, ownerFilter, sortBy, sortDir]);

    const handleSort = (col: SortCol) => {
        if (sortBy === col) {
            setSortDir(d => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(col);
            setSortDir("desc");
        }
    };

    const sortLabelSx = {
        color: "secondary.main",
        "&.Mui-active": { color: "secondary.main" },
        "& .MuiTableSortLabel-icon": { color: "secondary.main !important" },
        "&:hover": { color: "secondary.light" },
    };

    return (
        <Container fixed sx={{ py: 3 }}>
            <Typography align="center" variant="h5" color="secondary" fontWeight={600} gutterBottom>
                Таблица банд
            </Typography>

            {teams !== null && (
                <Typography align="center" variant="body1" color="white" gutterBottom>
                    {filtered.length} {plural(filtered.length, "банда", "банды", "банд")}
                    {filtered.length !== teams.length && (
                        <Typography component="span" color="rgba(255,255,255,0.4)" variant="body2">
                            {" "}(из {teams.length})
                        </Typography>
                    )}
                </Typography>
            )}

            {/* Фильтры */}
            {teams !== null && teams.length > 0 && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Поиск по названию"
                        size="small"
                        value={nameFilter}
                        onChange={e => setNameFilter(e.target.value)}
                        sx={{ ...darkInputSx, flex: 2 }}
                    />
                    <FormControl size="small" sx={{ ...darkInputSx, flex: 1 }}>
                        <InputLabel>Фракция</InputLabel>
                        <Select
                            value={factionFilter}
                            label="Фракция"
                            onChange={e => setFactionFilter(e.target.value)}
                            MenuProps={{ PaperProps: { sx: { bgcolor: "#2a2a2a", color: "white" } } }}
                        >
                            <MenuItem value="">Все</MenuItem>
                            {factionOptions.map(f => (
                                <MenuItem key={f} value={f}>{f}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ ...darkInputSx, flex: 1 }}>
                        <InputLabel>Владелец</InputLabel>
                        <Select
                            value={ownerFilter}
                            label="Владелец"
                            onChange={e => setOwnerFilter(e.target.value)}
                            MenuProps={{ PaperProps: { sx: { bgcolor: "#2a2a2a", color: "white" } } }}
                        >
                            <MenuItem value="">Все</MenuItem>
                            {ownerOptions.map(o => (
                                <MenuItem key={o} value={o}>{o}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            )}

            <Box
                sx={{
                    margin: "auto",
                    border: "solid",
                    borderColor: "rgba(255,255,255,0.4)",
                    background: "linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4))",
                    overflowX: "auto",
                }}>
                {teams === null ? (
                    <ContainerWithCircularProgress height="400px" />
                ) : filtered.length === 0 ? (
                    <Typography color="white" align="center" sx={{ py: 4 }}>
                        {teams.length === 0 ? "Нет данных" : "Ничего не найдено"}
                    </Typography>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={48} align="center" sx={headCellSx}>#</TableCell>
                                <TableCell sx={headCellSx}>Название</TableCell>
                                <TableCell sx={headCellSx}>Владелец</TableCell>
                                <TableCell sx={headCellSx}>Фракция</TableCell>
                                <TableCell align="center" sx={headCellSx}>
                                    <TableSortLabel
                                        active={sortBy === "rating"}
                                        direction={sortBy === "rating" ? sortDir : "desc"}
                                        onClick={() => handleSort("rating")}
                                        sx={sortLabelSx}>
                                        Рейтинг
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={headCellSx}>
                                    <TableSortLabel
                                        active={sortBy === "lastEditTime"}
                                        direction={sortBy === "lastEditTime" ? sortDir : "desc"}
                                        onClick={() => handleSort("lastEditTime")}
                                        sx={sortLabelSx}>
                                        Обновлено
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((team, index) => (
                                <TableRow
                                    key={team.id}
                                    sx={{ "&:hover": { background: "rgba(255,255,255,0.05)" } }}>
                                    <TableCell align="center" sx={{ ...cellSx, color: "rgba(255,255,255,0.4)" }}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <Link
                                            component={RouterLink}
                                            to={`/roster/${team.id}`}
                                            color="secondary"
                                            underline="always"
                                            fontWeight={500}>
                                            {team.name}
                                        </Link>
                                    </TableCell>
                                    <TableCell sx={cellSx}>{team.creator.name}</TableCell>
                                    <TableCell sx={cellSx}>{team.faction.name}</TableCell>
                                    <TableCell align="center" sx={cellSx}>
                                        <Chip label={team.rating} size="small" color="secondary" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ ...cellSx, color: "#645A59" }}>
                                        {getDate(team.lastEditTime)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
            </Box>
        </Container>
    );
}

function plural(n: number, one: string, few: string, many: string): string {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
    return many;
}
